import React, { useEffect, useState } from 'react'
import { getLocalizedValue } from './PageDetail'
import { useSEO } from '../lib/useSEO'

export default function Udtalelser() {
  const [page, setPage] = useState(null)
  const [udtalelser, setUdtalelser] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/by-slug/da/udtalelser-fra-vikarer').then(res => res.json()),
      fetch('https://api.dev.auxo.dk/public/content/udtalelser').then(res => res.json())
    ])
      .then(([pageData, listData]) => {
        setPage(pageData)
        setUdtalelser(listData)
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
      })
  }, [])

  useSEO({
    title: page ? getLocalizedValue(page.title) : 'Udtalelser fra vikarer · Vikar4',
    description: page ? (getLocalizedValue(page.meta_description) || getLocalizedValue(page.og_description) || 'Læs ærlige og autentiske udtalelser fra vores vikarer om deres oplevelser med Vikar4.') : 'Læs ærlige og autentiske udtalelser fra vores vikarer om deres oplevelser med Vikar4.',
    ogTitle: page ? getLocalizedValue(page.og_title) : 'Udtalelser fra vikarer · Vikar4',
    ogDescription: page ? (getLocalizedValue(page.og_description) || getLocalizedValue(page.meta_description)) : '',
    canonicalPath: '/udtalelser-fra-vikarer'
  })

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Page Header - Hero Banner Component */}
      <section className="relative h-[240px] md:h-[320px] bg-[#015D9A] flex items-center justify-center overflow-hidden">
        {/* Background Image with Cool-toned Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center mix-blend-multiply opacity-60 bg-[url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/7b6c8b8c-dd85-4faa-a1de-b9b48f1b2725/industrial-subpage-hero.jpg')]"
        />
        {/* Centered Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-wide">
            {getLocalizedValue(page?.title) || 'Udtalelser fra vikarer'}
          </h1>
        </div>
      </section>

      {/* Main Content - Two-Column Main Layout Content */}
      <main className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left: Content Column (Testimonials Grid) */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
              {udtalelser.map((item) => (
                <div
                  key={item.id}
                  className="relative flex flex-col justify-between rounded-sm border border-slate-100 bg-white p-8 shadow-none hover:border-[#015D9A] transition-all"
                >
                  {/* Quote Icon */}
                  <div className="absolute top-6 right-8 text-slate-100 text-6xl font-serif select-none pointer-events-none">
                    “
                  </div>

                  <div className="relative z-10">
                    <div
                      className="prose-custom text-base italic"
                      dangerouslySetInnerHTML={{ __html: getLocalizedValue(item.quote) }}
                    />
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-100 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-[#015D9A] font-bold text-sm">
                      {getLocalizedValue(item.name).charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0f3d5c]">
                        {getLocalizedValue(item.name)}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {getLocalizedValue(item.role)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Sidebar Action Box */}
          <aside className="lg:col-span-4">
            <div className="bg-slate-100 p-8 text-center flex flex-col items-center justify-center border border-slate-200/50 rounded-sm shadow-none">
              <h3 className="text-lg font-bold text-[#0f3d5c] mb-6">
                Ønsker du at blive vikar?
              </h3>
              {/* Linear Brand Icon */}
              <div className="flex justify-center mb-6 text-[#015D9A] w-40 h-28">
                <svg className="w-full h-full" fill="none" viewBox="0 0 100 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {/* Left User Back */}
                  <path d="M 15 45 C 15 35, 35 35, 35 45 Z" />
                  <circle cx="25" cy="28" r="7" />
                  {/* Right User Back */}
                  <path d="M 65 45 C 65 35, 85 35, 85 45 Z" />
                  <circle cx="75" cy="28" r="7" />
                  {/* Middle Main User Front (Larger) */}
                  <path className="stroke-3" fill="#f1f5f9" d="M 30 52 C 30 38, 70 38, 70 52 Z" />
                  <circle className="stroke-3" fill="#f1f5f9" cx="50" cy="28" r="9" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Hvis du ønsker at blive vikar, så skriv en mail til
                <a href="mailto:job@vikar4.dk" className="block mt-1 font-bold text-[#015D9A] hover:text-[#014d80] hover:underline break-all">
                  job@vikar4.dk
                </a>
              </p>
            </div>
          </aside>

        </div>
      </main>
    </div>
  )
}
